import PhoneNumberInput, { Labels } from "react-phone-number-input";
import { isSupportedLanguageCode, SupportedLanguageCode } from "../../../../i18n";
import { corners, gap, marginLeft, marginRight, padding, paddingX, typography } from "../../../styles";
import { Error, ErrorConnector, HelpText, Label, RequiredStar } from "../common";
import { E164Number } from "libphonenumber-js/types";
import React from "react";
import { useTranslation } from "react-i18next";
import ar from "react-phone-number-input/locale/ar.json";
import cz from "react-phone-number-input/locale/cz.json";
import de from "react-phone-number-input/locale/de.json";
import el from "react-phone-number-input/locale/el.json";
import en from "react-phone-number-input/locale/en.json";
import es from "react-phone-number-input/locale/es.json";
import fi from "react-phone-number-input/locale/fi.json";
import fr from "react-phone-number-input/locale/fr.json";
import it from "react-phone-number-input/locale/it.json";
import ja from "react-phone-number-input/locale/ja.json";
import nb from "react-phone-number-input/locale/nb.json";
import nl from "react-phone-number-input/locale/nl.json";
import pl from "react-phone-number-input/locale/pl.json";
import pt from "react-phone-number-input/locale/pt.json";
import sv from "react-phone-number-input/locale/sv.json";
import styled from "styled-components";
import { applyOpacity } from "../../../styles/utils";
import { SharedInputProps } from "../Input";

/**
 * Unlike other inputs, Phone inputs require external value handling with an
 * onChange handler, because react-phone-number-input requires it.
 */
export type PhoneProps = Omit<SharedInputProps, "onChange"> & {
  defaultCountry?: string;
  onChange: (newPhoneNumber: E164Number) => void;
  value?: E164Number;
};
const Input = styled(PhoneNumberInput)<{
  $error: boolean;
}>`
  display: flex;
  height: 2rem;

  ${gap.s}

  input {
    ${typography.body}
    width: 100%;
    border: 1px solid
      ${({
  $error,
  theme
}) => $error ? theme.colours.error : applyOpacity(theme.colours.dark, 0.6)};
    ${corners.s}
    ${padding.xs}
  }

  .PhoneInputCountry {
    display: flex;
    position: relative;
    align-items: center;

    background: ${({
  theme
}) => theme.colours.white};
    border: 1px solid
      ${({
  $error,
  theme
}) => $error ? theme.colours.error : applyOpacity(theme.colours.dark, 0.6)};
    ${corners.s}
    ${paddingX.s}

    &:focus-within {
      outline: 5px auto Highlight;
      outline: 5px auto -webkit-focus-ring-color;
    }

    .PhoneInputCountryIcon {
      width: 1.5rem;
      height: 1rem;
    }

    .PhoneInputCountryIcon--border {
      box-shadow:
        0 0 0 1px ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)},
        inset 0 0 0 1px ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
    }

    .PhoneInputCountrySelectArrow {
      content: url("data:image/svg+xml; utf8, <svg width='12' height='7' viewBox='0 0 12 7' fill='none' xmlns='http://www.w3.org/2000/svg'><g id='Icon/claret'><path id='Vector' d='M0.807433 0H11.1926C11.911 0 12.2702 0.867782 11.7617 1.37634L6.57112 6.57093C6.2563 6.88575 5.7437 6.88575 5.42888 6.57093L0.23833 1.37634C-0.270231 0.867782 0.0889903 0 0.807433 0Z' fill='%231a1a1a'/></g></svg>");
      width: 0.5rem;
      height: 0.5rem;
      ${marginLeft.xs}
      ${marginRight.xs}
    }

    select {
      width: 2.5rem;
      position: absolute;
      z-index: 1;
      border: 0;
      opacity: 0;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
      }
    }
  }
`;
const labelMap: Record<SupportedLanguageCode, Labels> = {
  ar,
  cs: cz,
  da: en,
  // no Danish labels available in react-phone-number-input
  de,
  el,
  "en-GB": en,
  "en-US": en,
  es,
  fi,
  fr,
  hu: en,
  // no Hungarian labels available in react-phone-number-input
  it,
  ja,
  nb,
  nl,
  pl,
  pt,
  ro: en,
  // no Romanian labels available in react-phone-number-input
  sv,
  zh: en // no Chinese labels available in react-phone-number-input
};
export function Phone({
  error,
  helpText,
  label,
  ...props
}: PhoneProps) {
  const {
    id,
    required
  } = props;
  const hasError = !!error;
  const {
    i18n
  } = useTranslation();
  const locale = i18n.language;

  // Not really possible to sensibly test for actual languages as tests must use "cimode"
  /* istanbul ignore next */
  const labels = isSupportedLanguageCode(locale) ? labelMap[locale] : en;
  return <>
      <Label htmlFor={id} data-sentry-element="Label" data-sentry-source-file="Phone.tsx">
        {label}
        {required ? <RequiredStar /> : null}
      </Label>
      <HelpText data-sentry-element="HelpText" data-sentry-source-file="Phone.tsx">{helpText}</HelpText>
      <ErrorConnector show={hasError} data-sentry-element="ErrorConnector" data-sentry-source-file="Phone.tsx">
        <Input id={id} aria-required={required} dir={i18n.dir()} {...{
        ...props,
        $error: hasError,
        focusInputOnCountrySelection: false,
        labels,
        limitMaxLength: true
      }} data-sentry-element="Input" data-sentry-source-file="Phone.tsx" />
        {error ? <Error>{error}</Error> : null}
      </ErrorConnector>
    </>;
}
export default Phone;